/**
 * quickCreateRows — the settings rows of the quick-create screen (W12 Part B,
 * Task B7).
 *
 * Pure: rail items in, rows out. No React, no services.
 *
 * The rule this module exists to enforce: a quick-create row is a
 * RE-PRESENTATION of the wizard, never a second opinion about it. Every value
 * is the caption `buildWizardRailItems` already computed for the same step, so
 * the two surfaces can never disagree about what the request carries (FP-7).
 * The `detail` line is added ONLY where the value cannot say it — which routes,
 * how the variable package is composed, what the social proof is made of, what
 * the run is estimated to cost.
 */

import {
  ESTIMATE_UNAVAILABLE_DETAIL,
  formatEstimate,
  type GenerationEstimate,
} from './generationEstimate';
import type { WizardRailItem, WizardStepKey } from './wizardSteps';

/** Every wizard step that owns a SETTING, plus the review step's screens switch. */
export type QuickCreateRowKey = Exclude<WizardStepKey, 'analysis' | 'review'> | 'screens';

export interface QuickCreateRow {
  key: QuickCreateRowKey;
  label: string;
  /** The rail caption of the owning step («3 of 3 routes»). */
  value: string;
  /** What the value cannot say — route names, composition, the estimate. */
  detail?: string;
}

/**
 * The step a row's «Change» opens. Identity for the eight settings steps;
 * «Screens» is decided on the REVIEW step (that is where the switch lives), so
 * it routes there (owner ruling).
 */
export const QUICK_CREATE_ROW_STEP: Record<QuickCreateRowKey, WizardStepKey> = {
  tasks: 'tasks',
  insights: 'insights',
  insightVars: 'insightVars',
  projectVars: 'projectVars',
  proof: 'proof',
  details: 'details',
  blockTypes: 'blockTypes',
  library: 'library',
  generation: 'generation',
  screens: 'review',
};

/**
 * Row labels — the approved canvas v3.3 copy (`QuickCreate.html`). They are
 * NOT the rail labels: the rail is ~118px wide and abbreviates
 * («Insight variables»), while a 206px row label can name the thing in full.
 */
const QUICK_CREATE_ROW_LABELS: Record<QuickCreateRowKey, string> = {
  tasks: 'Tasks',
  insights: 'Insights',
  insightVars: 'Insight-derived variables',
  projectVars: 'Project variables',
  proof: 'Social proof',
  details: 'Details & tone',
  blockTypes: 'Block types',
  library: 'Building blocks',
  generation: 'AI settings',
  screens: 'Screens',
};

/** Row order — the approved mock's order, which is the wizard's order. */
const QUICK_CREATE_ROW_ORDER: QuickCreateRowKey[] = [
  'tasks', 'insights', 'insightVars', 'projectVars', 'proof',
  'details', 'blockTypes', 'library', 'generation', 'screens',
];

export interface SocialProofCounts {
  reviews: number;
  ratings: number;
  studyCases: number;
  bigNumbers: number;
}

const plural = (count: number, singular: string, pluralForm: string): string =>
  `${count} ${count === 1 ? singular : pluralForm}`;

/**
 * «5 reviews · 2 ratings · 3 study cases · 4 big numbers» — only the kinds the
 * request actually carries. `undefined` when nothing is selected: the value
 * («0») already says that, and an empty list of kinds would say it twice.
 */
export const socialProofDetail = (counts: SocialProofCounts): string | undefined => {
  const parts = [
    counts.reviews > 0 ? plural(counts.reviews, 'review', 'reviews') : null,
    counts.ratings > 0 ? plural(counts.ratings, 'rating', 'ratings') : null,
    counts.studyCases > 0 ? plural(counts.studyCases, 'study case', 'study cases') : null,
    counts.bigNumbers > 0 ? plural(counts.bigNumbers, 'big number', 'big numbers') : null,
  ].filter(Boolean) as string[];
  return parts.length > 0 ? parts.join(' · ') : undefined;
};

export interface BuildQuickCreateRowsInput {
  /** `buildWizardRailItems(...)` output — the ONE caption source. */
  rail: WizardRailItem[];
  /** Task routes in the request — the estimate's size input. */
  routes: number;
  generateScreens: boolean;
  /** `estimateGeneration(...)` for those routes and that switch. */
  estimate: GenerationEstimate;
  /** Names of the SELECTED routes — the «Tasks» detail. */
  routeNames: string[];
  /** The variable-package composition caption the wizard step already shows. */
  projectVariablesSummary?: string;
  /**
   * W13 IS2 (I3): what the thesis caption cannot say on its own — today only the
   * READ FAILURE («… could not be read — the default projection will be used»),
   * which the quick screen must name as loudly as the wizard does.
   */
  insightsDetail?: string;
  /** Submitted social proof, per kind. */
  socialProof: SocialProofCounts;
}

export function buildQuickCreateRows(input: BuildQuickCreateRowsInput): QuickCreateRow[] {
  const captionOf = (key: WizardStepKey): string =>
    input.rail.find((item) => item.key === key)?.caption ?? '—';

  const formatted = formatEstimate(input.estimate, input.routes);
  const screensValue = input.generateScreens
    ? `With screens · ${formatted.screens}`
    : 'Plan only';
  // Honesty rule (Task B6): an estimate that cannot be computed names the
  // reason instead of a number, and the cost is always labelled *estimated*.
  const screensDetail = input.estimate.screens === null
    ? ESTIMATE_UNAVAILABLE_DETAIL
    : input.estimate.usd === null
      ? undefined
      : `${formatted.cost} — estimated`;

  const detailOf = (key: QuickCreateRowKey): string | undefined => {
    switch (key) {
      case 'tasks':
        return input.routeNames.length > 0 ? input.routeNames.join(' · ') : undefined;
      case 'insights':
        return input.insightsDetail || undefined;
      case 'projectVars':
        return input.projectVariablesSummary || undefined;
      case 'proof':
        return socialProofDetail(input.socialProof);
      case 'screens':
        return screensDetail;
      default:
        return undefined;
    }
  };

  return QUICK_CREATE_ROW_ORDER.map((key) => ({
    key,
    label: QUICK_CREATE_ROW_LABELS[key],
    value: key === 'screens' ? screensValue : captionOf(key),
    detail: detailOf(key),
  }));
}
